/* eslint-disable camelcase */
import { invisiblyApi } from '../../../redux/api/invisibly-api';
import { InvisiblyApiResponse } from '../../../services/api/api-response.types';
import { APITagType } from '../../../redux/api/invisibly-api.types';
import type {
  PacingBoardData,
  PacingBoardShare,
  PacingBoardSummary,
  PacingBoardUser,
  PacingBudget,
  PacingBudgetFormData,
} from '../types/pacing-board.types';
import type { PacingLookupResponse, PacingLookupType } from './pacing-lookup.types';

const pacingBoardEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getPacingBoards: builder.query<PacingBoardSummary[], void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/pacing/boards',
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBoardSummary[]>) {
        return response.item;
      },
      providesTags: [APITagType.PacingBoard],
    }),
    getPacingBoard: builder.query<PacingBoardData, string>({
      query: (boardId) => ({
        method: 'GET',
        url: `b2b/pacing/boards/${boardId}`,
        timeout: 60000,
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBoardData>) {
        return response.item;
      },
      providesTags: (_result, _error, boardId) => [{ type: APITagType.PacingBoard, id: boardId }],
    }),
    createPacingBoard: builder.mutation<PacingBoardSummary, { name: string }>({
      query: ({ name }) => ({
        method: 'POST',
        url: 'b2b/pacing/boards',
        data: { name },
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBoardSummary>) {
        return response.item;
      },
      invalidatesTags: [APITagType.PacingBoard],
    }),
    updatePacingBoard: builder.mutation<PacingBoardSummary, { boardId: string; name: string }>({
      query: ({ boardId, name }) => ({
        method: 'PATCH',
        url: `b2b/pacing/boards/${boardId}`,
        data: { name },
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBoardSummary>) {
        return response.item;
      },
      invalidatesTags: [APITagType.PacingBoard],
    }),
    deletePacingBoard: builder.mutation<void, string>({
      query: (boardId) => ({
        method: 'DELETE',
        url: `b2b/pacing/boards/${boardId}`,
      }),
      invalidatesTags: [APITagType.PacingBoard],
    }),
    sharePacingBoard: builder.mutation<PacingBoardShare, { boardId: string; userId: string; role: 'view' | 'edit' }>({
      query: ({ boardId, userId, role }) => ({
        method: 'POST',
        url: `b2b/pacing/boards/${boardId}/share`,
        data: { user_id: userId, role },
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBoardShare>) {
        return response.item;
      },
      invalidatesTags: (_r, _e, { boardId }) => [{ type: APITagType.PacingBoard, id: boardId }],
    }),
    revokePacingBoardShare: builder.mutation<void, { boardId: string; userId: string }>({
      query: ({ boardId, userId }) => ({
        method: 'POST',
        url: `b2b/pacing/boards/${boardId}/revoke-share`,
        data: { user_id: userId },
      }),
      invalidatesTags: (_r, _e, { boardId }) => [{ type: APITagType.PacingBoard, id: boardId }],
    }),
    getPacingBoardShareableUsers: builder.query<PacingBoardUser[], void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/pacing/boards/shareable-users',
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBoardUser[]>) {
        return response.item;
      },
    }),
    getPacingLookup: builder.query<PacingLookupResponse, { type: PacingLookupType; q?: string }>({
      query: ({ type, q }) => ({
        method: 'GET',
        url: 'b2b/operations/pacing/lookup',
        params: {
          type,
          q: q ?? '',
        },
      }),
      transformResponse(response: InvisiblyApiResponse<PacingLookupResponse>) {
        return response.item;
      },
      keepUnusedDataFor: 600,
    }),
    createPacingBudget: builder.mutation<PacingBudget, PacingBudgetFormData>({
      query: (data) => ({
        method: 'POST',
        url: 'b2b/pacing/budgets',
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBudget>) {
        return response.item;
      },
      invalidatesTags: [APITagType.PacingBoard],
    }),
    updatePacingBudget: builder.mutation<PacingBudget, { budgetId: string; data: Partial<PacingBudgetFormData> }>({
      query: ({ budgetId, data }) => ({
        method: 'PATCH',
        url: `b2b/pacing/budgets/${budgetId}`,
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<PacingBudget>) {
        return response.item;
      },
      invalidatesTags: [APITagType.PacingBoard],
    }),
    deletePacingBudget: builder.mutation<void, string>({
      query: (budgetId) => ({
        method: 'DELETE',
        url: `b2b/pacing/budgets/${budgetId}`,
      }),
      invalidatesTags: [APITagType.PacingBoard],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetPacingBoardsQuery,
  useGetPacingBoardQuery,
  useCreatePacingBoardMutation,
  useUpdatePacingBoardMutation,
  useDeletePacingBoardMutation,
  useSharePacingBoardMutation,
  useRevokePacingBoardShareMutation,
  useGetPacingBoardShareableUsersQuery,
  useLazyGetPacingLookupQuery,
  useCreatePacingBudgetMutation,
  useUpdatePacingBudgetMutation,
  useDeletePacingBudgetMutation,
} = pacingBoardEndpoints;
